/**
 * Invoice Management Enums and Constants
 * Centralized reusable strings for invoice management components
 */

// Filter and Default Values
export const INVOICE_FILTER_OPTIONS = {
  ALL_SUPPLIERS: 'All Suppliers',
  ALL_STATUS: 'All Status',
  ALL_PROJECTS: 'All Projects',
  ALL_TIME: 'All Time',
  CUSTOM: 'Custom',
  TODAY: 'Today',
  THIS_WEEK: 'This Week',
  THIS_MONTH: 'This Month',
  LAST_MONTH: 'Last Month',
  THIS_QUARTER: 'This Quarter',
  LAST_QUARTER: 'Last Quarter',
  THIS_YEAR: 'This Year',
  LAST_YEAR: 'Last Year',
  LAST_30_DAYS: 'Last 30 days',
  LAST_90_DAYS: 'Last 90 days',
  PAYMENT_STATUS: 'Payment Status',
  ALL_PAYMENT_STATUS: 'All Payment Status',
  FULLY_INVOICED: 'Fully Invoiced',
  PARTIALLY_INVOICED: 'Partially Invoiced',
  NOT_INVOICED: 'Not Invoiced',
};

export const FILTER_KEYS = {
  PO: 'poId',
  SUPPLIER: 'supplierId',
  SECTION: 'sectionId',
  MONTHORPERIOD: 'monthPeriod',
  QUARTER: 'quarterPeriod',
  ID: 'id',
  SELECTED_PROJECT: 'selectedProjectId',
  SELECTED_STAGE: 'selectedStageId',
  SELECTED_TASK: 'selectedTaskId',
};

export const FILTER_KEYS_VALUES = {
  EMPTY_ISSUE_DATE: 'empty-issue-date'
};

// Grouping Options
export const INVOICE_GROUPING_OPTIONS = {
  MONTH: 'Group by Month',
  PROJECT: 'Group by Project',
  PO: 'Group by PurchaseOrders',
  QUARTER: 'Group by Quarter',
  SECTION: 'Group by Section\\SubSection',
  SUPPLIER: 'Group by Supplier',
  NONE: 'No Grouping'
};

// Grouping String Values
export const INVOICE_GROUPING_VALUES = {
  MONTH: 'month',
  PROJECT: 'project',
  PO: 'po',
  QUARTER: 'quarter',
  SECTION: 'section',
  SUPPLIER: 'supplier',
  NONE: 'none',
  TASK: 'task',
  INVOICES: 'invoices',
  SUPPLIER_INVOICES: 'supplierInvoices',
  MONTH_INVOICES: 'monthInvoices',
  QUARTER_INVOICES: 'quarterInvoices'
};

export const BREADCRUMB_ITEMS_TYPES = {
  PROJECT: 'project',
  STAGE: 'stage',
};

export const BREADCRUMB_ITEMS_LABEL = {
  PROJECT: 'All Projects',
  STAGE: 'All Stages',
  TASK: 'All Tasks'
};
export const TABLE_BREADCRUMB_ITEMS = {
  PROJECT: 'Projects',
  SECTION: 'Sections',
  PO: 'PurchaseOrders',
  SUPPLIER: 'Suppliers',
  MONTH: 'Months',
  QUARTER: 'Quarters',
  INVOICE: 'Invoices'
};
// Status Values
export const INVOICE_STATUS = {
  PENDING: 'PENDING',
  PAID: 'PAID',
  PARTIAL_PAID: 'PARTIAL_PAID',
  COMPLETED: 'Completed',
  NA: 'N/A'
};

// Special Identifiers
export const INVOICE_SPECIAL_IDS = {
  VARIATIONS: 'variations',
  UNAMED_VARIATION: 'Unnamed Variation'
};

// Error Messages
export const INVOICE_ERROR_MESSAGES = {
  UNKNOWN_PROJECT: 'Unknown Project',
  UNKNOWN_SECTION: 'Unknown Section',
  UNKNOWN_SUPPLIER: 'Unknown Supplier',
  NO_SECTIONS: 'No Sections available.',
  NO_TASKS: 'No Task available.',
  NO_VARIATIONS: 'No variations available.',
  NO_MATCHING_ITEMS: 'No matching projects, sections, tasks, or variations.',
  NO_PROJECTS: 'No projects available.',
  PROJECT_NOT_FOUND: 'Project not found.',
  STAGE_NOT_FOUND: 'Stage not found.',
  TASK_NOT_FOUND: 'Task or variation not found.',
  NO_ITEMS: 'No items available.',
  NO_CHART_DATA: 'No data to display chart for this grouping/filter.',
  INVOICE_DETAILS_ERROR: 'Error displaying invoice details. Please try again later.',
  LOAD_INVOICE_ERROR: 'Failed to load invoice data. Please try refreshing the page.',
  LOAD_TABLE_ERROR: 'Failed to load table data. Please try refreshing the page.',
  APPLY_FILTER_ERROR: 'Failed to apply filters. Please refresh the page and try again.'
};

// UI Labels and Text
export const INVOICE_UI_LABELS = {
  PROJECT_MENU: 'Project Menu',
  SEARCH_PLACEHOLDER: 'Search projects, sections, tasks, variations...',
  APPLY_FILTERS: 'Apply Filters',
  CLOSE: 'Close',
  FILTER_INVOICES: 'Filter Invoices',
  ADD_FILTERS: 'Add Filters',
  CLEAR_FILTERS: 'Clear Filters',
  BACK_TO_CHART: 'Back to previous chart view',
  GROUPS: 'Groups',
  SUPPLIER_INVOICE_MANAGEMENT: 'Supplier Invoice Management'
};

// Table Column Headers
export const INVOICE_TABLE_COLUMNS = {
  INVOICE_NUMBER: 'Invoice Number',
  PROJECT: 'Project',
  PROJECT_NAME: 'Project Name',
  SECTION_SUBSECTION: 'Section\\SubSection',
  PO_NUMBER: 'PO Number',
  PROCUREMENT_CODE: 'Procurement Code',
  SUPPLIER: 'Supplier',
  VENDOR: 'Vendor',
  ISSUE_DATE: 'Issue Date',
  DUE_DATE: 'Due Date',
  AMOUNT: 'Amount',
  INVOICE_AMOUNT: 'Invoice Amount',
  TOTAL: 'Total',
  SUB_TOTAL: 'Sub Total',
  INVOICE_SUBTOTAL: 'Invoice SubTotal',
  RECEIVED_BY: 'Received By',
  TOTAL_INVOICE_AMOUNT: 'Total Invoice Amount',
  INVOICE_COUNT: 'Invoice Count',
  PO_TOTAL: 'PO Total',
  PO_COUNT: 'PO Count',
  MATERIAL_STATUS: 'Material Status',
  INVOICE_STATUS: 'Invoice Status',
  STATUS: 'Status',
  ADDRESS: 'Address',
  MONTH: 'Month',
  QUARTER: 'Quarter'
};

// Chart Labels and Tooltips
export const INVOICE_CHART_LABELS = {
  TOTAL_INVOICE_AMOUNT: 'Total Invoice Amount',
  INVOICE_AMOUNTS: 'Invoice Amounts',
  TOTAL_INVOICE_AMOUNT_BY_PROJECT: 'Total Invoice Amount by Project',
  TOTAL_INVOICE_AMOUNT_BY_SECTION: 'Total Invoice Amount by Section\\SubSection',
  TOTAL_INVOICE_AMOUNT_BY_SUPPLIER: 'Total Invoice Amount by Supplier',
  TOTAL_INVOICE_AMOUNT_BY_MONTH: 'Total Invoice Amount by Month',
  TOTAL_INVOICE_AMOUNT_BY_QUARTER: 'Total Invoice Amount by Quarter',
  TOTAL_INVOICE_AMOUNT_BY_PO: 'Total Invoice Amount by Purchase Order',
  INVOICE_STATUS_DISTRIBUTION: 'Invoice Status Distribution'
};

export const INVOICE_CHART_BACK_BUTTON_LABELS = {
  PO_S: 'POs',
  GROUPS: 'Groups',
  BACK_TO_CHART: 'Back to previous chart view'
};

// Chart Tooltip Fields
export const INVOICE_CHART_TOOLTIPS = {
  PROJECT: 'Project',
  SECTION: 'Section',
  PO_NUMBER: 'PO Number',
  PROCUREMENT_CODE: 'Procurement Code',
  VENDOR: 'Vendor',
  PO_TOTAL: 'PO Total',
  INVOICE_AMOUNT: 'Invoice Amount',
  INVOICE_SUBTOTAL: 'Invoice Subtotal',
  INVOICE_COUNT: 'Invoice Count',
  PO_MATERIAL_STATUS: 'PO Material Status',
  INVOICE_STATUS: 'Invoice Status',
  INVOICES: 'Invoices',
  POS: 'POs',
  TOTAL_AMOUNT: 'Total Amount',
  SUBTOTAL: 'Subtotal',
  PERCENTAGE: 'Percentage',
  INVOICE_NUMBER: 'Invoice Number',
  PO_COUNT: 'PO Count',
  SUPPLIER: 'Supplier'
};

export const INVOICE_CHART_DRILL_DOWN_STATE = {
  INVOICE_DETAILS: 'invoice-details',
  PO_DETAILS: 'po-details',
  PROJECT_DETAILS: 'project-details',
  VIEW_INVOICE_MODAL: 'view-invoice-modal',
  GROUP: 'group',
  SECTION: 'section',
  PO_INVOICES: 'po-invoices',
  SUPPLIER_INVOICES: 'supplier-invoices',
  MONTH_INVOICES: 'month-invoices',
  QUARTER_INVOICES: 'quarter-invoices'
};

// CSS Classes
export const INVOICE_CSS_CLASSES = {
  TEXT_WARNING: 'text-warning',
  TEXT_SUCCESS: 'text-success',
  AMOUNT: 'amount',
  SUB_TOTAL_AMOUNT: 'sub-total amount',
  SUPPLIER_NAME: 'supplier-name',
  INVOICE_NUMBER: 'invoice-number',
  PO_LINK: 'po-link',
  PROJECT_LINK: 'project-link',
  SECTION_LINK: 'section-link',
  PERIOD_LINK: 'period-link',
  SUPPLIER_LINK: 'supplier-link'
};

export const TABLE_GROUP_TYPES = {
  PROJECT: 'group-project',
  SECTION: 'group-section',
  PO: 'group-po',
  SUPPLIER: 'group-supplier',
  MONTH: 'group-month',
  QUARTER: 'group-quarter',
  NONE: 'group-none'
};

export const INVOICE_DATE_FORMAT = 'DD/MM/YYYY';
export const INVOICE_MONTH_FORMAT = 'YYYY-MM';
